import { FormFieldDateRangeValue, FormFieldValue } from "../../../interface";
import { toSelectedValueArray, toUsDateString } from "../../../shared-modules/form-builder/form-field/form-field.util";
import { DATE_RANGE_KEY, DATE_RANGE_LABEL, PATIENT_FILTERS_DEFAULT } from "./previous-cases-filters.constants";
import { ActiveFilterBadge, FilterSectionConfig, FilterSectionOption, HospitalStatusKey, PatientsFilters, PatientsFiltersValues, SnfStatusKey } from "./previous-cases-filters.interface";

const LEGACY_SNF_STATUS_KEYS: SnfStatusKey[] = ['new', 'inProgress', 'accepted', 'declined'];
const LEGACY_HOSPITAL_STATUS_KEYS: HospitalStatusKey[] = ['selected', 'notSelected'];

// Saved filters can carry hospital buckets in the pre-SNF-511 `status` key or in an earlier snfStatus; both move to hospitalStatus.
export function migrateLegacyStatusFilter(filters: PatientsFilters): PatientsFilters {
  const { status, snfStatus, hospitalStatus, ...rest } = filters ?? {};
  const statusKeys: string[] = [...asArray(status), ...asArray(snfStatus)];
  const hospitalKeys: string[] = [...statusKeys, ...asArray(hospitalStatus)];
  const snf = LEGACY_SNF_STATUS_KEYS.filter(key => statusKeys.includes(key));
  const hospital = LEGACY_HOSPITAL_STATUS_KEYS.filter(key => hospitalKeys.includes(key));
  return {
    ...rest,
    ...(snf.length ? { snfStatus: snf } : {}),
    ...(hospital.length ? { hospitalStatus: hospital } : {}),
  };
}

function asArray<T>(value: T[] | T | null | undefined): T[] {
  return value == null ? [] : Array.isArray(value) ? value : [value];
}


// Server filter keys carried by the single dateRange form field. `filterByDate` is the
// pre-SNF-512 checkbox that gated them - not on PatientsFilters, but the server stores
// whatever the list request sends, so it comes back in the saved filters.
export const DATE_FILTER_KEYS = ['filterByDate', 'firstPostingDateStart', 'firstPostingDateEnd'];

// The server persists the filters of every list request and replays them into the query, so
// a date range outlives the session and follows the user to another device. The range is
// session-only by product decision: drop it whenever saved filters are read back.
export function stripDateFilters<T extends Record<string, any>>(filters: T): T {
  return Object.keys(filters)
    .filter(key => !DATE_FILTER_KEYS.includes(key))
    .reduce((acc, key) => { acc[key] = filters[key]; return acc; }, {} as Record<string, any>) as T;
}

// sessionStorage, not localStorage: the range has to survive a refresh but never reach
// another tab or outlive the login (goToLogin clears it).
const DATE_FILTER_SESSION_KEY = 'previousCases.dateFilters';
const MAX_BADGE_NAMES = 4;

export function readSessionDateFilters(): Record<string, any> {
  try {
    const raw = sessionStorage.getItem(DATE_FILTER_SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed?.filterByDate ? parsed : {};
  } catch {
    return {};
  }
}

export function writeSessionDateFilters(values: Record<string, any>): void {
  try {
    if (!values?.filterByDate) {
      sessionStorage.removeItem(DATE_FILTER_SESSION_KEY);
      return;
    }
    const dates = DATE_FILTER_KEYS
      .filter(key => values[key] !== undefined && values[key] !== '')
      .reduce((acc, key) => { acc[key] = values[key]; return acc; }, {} as Record<string, any>);
    sessionStorage.setItem(DATE_FILTER_SESSION_KEY, JSON.stringify(dates));
  } catch {
    // storage blocked (private mode): the range just falls back to page-lifetime only
  }
}

export function clearSessionDateFilters(): void {
  try {
    sessionStorage.removeItem(DATE_FILTER_SESSION_KEY);
  } catch {
  }
}

export function mapFormToFilters(values: PatientsFiltersValues): PatientsFilters {
  const filters: PatientsFilters = {};
  Object.entries(values).forEach(([key, selectedByValue]) => {
    // form-builder seeds an untouched field with '' (initValues), so an unset multi-select would
    // otherwise be persisted as a string where an array is expected. Omit it instead — '' carries
    // no filter for any key, including the cleared date fields.
    // null is checked too because typeof null === 'object' would send it into toSelectedValueArray.
    if (selectedByValue === '' || selectedByValue === null) {
      return;
    }
    if (key === 'dateRange') {
      const { start, end } = selectedByValue as { start?: string; end?: string };
      // Dates filter only as a complete range - drop a half-filled one
      if (start && end) {
        filters.firstPostingDateStart = start;
        filters.firstPostingDateEnd = end;
      }
      return;
    }
    if (typeof selectedByValue !== 'object') {
      filters[key] = selectedByValue;
      return;
    }
    const values = toSelectedValueArray(selectedByValue);
    if (values.length === 0) {
      return;
    }
    filters[key] = values;
  });
  return filters;
}

export function mapFiltersToForm(filters: PatientsFilters): PatientsFiltersValues {
  const values: PatientsFiltersValues = { ...PATIENT_FILTERS_DEFAULT };
  Object.entries(filters).forEach(([key, valueArray]) => {
    if (DATE_FILTER_KEYS.includes(key)) {
      return;
    }
    if (typeof valueArray !== 'object' && valueArray !== undefined) {
      values[key] = valueArray;
      return;
    }
    if (!valueArray || valueArray.length === 0) {
      return;
    }
    values[key] = {};
    valueArray.forEach(value => values[key][value] = true);
  });
  if (filters.firstPostingDateStart && filters.firstPostingDateEnd) {
    values.dateRange = { start: filters.firstPostingDateStart, end: filters.firstPostingDateEnd };
  }
  return values;
}
export function normalizeSelection(saved: Record<string, boolean> | null | undefined, all: string[]): string[] {
  if (!saved || typeof saved !== 'object') {
    return [...all];
  }
  return all.filter(value => saved[value]);
}

export function toLegacySelectionRecord(selected: string[]): Record<string, boolean> {
  return selected.reduce((acc, value) => { acc[value] = true; return acc; }, {} as Record<string, boolean>);
}

export function isSelectionActive(section: FilterSectionConfig, options: FilterSectionOption[], selected: string[]): boolean {
  if (!selected.length) {
    return false;
  }
  return !(section.selectAll && selected.length === options.length);
}

export function buildActiveBadges(sections: FilterSectionConfig[], selectedByKey: Record<string, string[]>): ActiveFilterBadge[] {
  return sections.reduce((badges, section) => {
    const selected = selectedByKey[section.key] ?? [];
    if (!isSelectionActive(section, section.options, selected)) {
      return badges;
    }
    const labelByValue = new Map(section.options.map(({ value, label }) => [value, label]));
    const names = selected.map(value => labelByValue.get(value) ?? value).join(', ');
    const counted = !!section.countNoun && selected.length > MAX_BADGE_NAMES;
    badges.push({
      sectionKey: section.key,
      sectionTitle: section.title,
      label: counted ? `${selected.length} ${section.countNoun} filtered` : names,
      ...(counted ? { detail: names } : {}),
      ...(section.tone ? { tone: section.tone } : {}),
    });
    return badges;
  }, [] as ActiveFilterBadge[]);
}

function isCompleteDateRange(value: FormFieldValue | null | undefined): value is FormFieldDateRangeValue {
  return typeof value === 'object' && value !== null && typeof value['start'] === 'string' && !!value['start'] && !!value['end'];
}

export function buildDateRangeBadge(range: FormFieldValue | null | undefined): ActiveFilterBadge | null {
  if (!isCompleteDateRange(range)) {
    return null;
  }
  return { sectionKey: DATE_RANGE_KEY, sectionTitle: DATE_RANGE_LABEL, label: `${toUsDateString(range.start)} - ${toUsDateString(range.end)}` };
}
